import { useEffect, useRef, useState } from 'react';

interface QRGeneratorProps {
  url: string;
  label?: string;
  size?: number;
  downloadName?: string;
}

const QRGenerator = ({ url, label, size = 200, downloadName = 'qr-islavende' }: QRGeneratorProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrLoaded, setQrLoaded] = useState(false);
  const [qrError, setQrError] = useState(false);

  useEffect(() => {
    if (!url) return;
    setQrLoaded(false);
    setQrError(false);

    // Usamos la API de QR de Google Charts (no requiere npm)
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}&bgcolor=ffffff&color=1a1a1a&margin=10`;

    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      canvas.width = size;
      canvas.height = size;
      ctx.drawImage(img, 0, 0, size, size);
      setQrLoaded(true);
    };

    img.onerror = () => {
      setQrError(true);
    };
  }, [url, size]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${downloadName}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="bg-white border-2 border-gray-200 rounded-xl p-3 relative">
        {!qrLoaded && !qrError && (
          <div
            style={{ width: size, height: size }}
            className="flex items-center justify-center bg-gray-50 rounded-lg"
          >
            <div className="w-8 h-8 border-3 border-gray-300 border-t-gray-700 rounded-full animate-spin"></div>
          </div>
        )}
        {qrError && (
          <div
            style={{ width: size, height: size }}
            className="flex flex-col items-center justify-center bg-gray-50 rounded-lg gap-2"
          >
            <i className="ri-qr-code-line text-4xl text-gray-400"></i>
            <p className="text-xs text-gray-500 text-center px-2">Error generando QR</p>
          </div>
        )}
        <canvas
          ref={canvasRef}
          style={{ display: qrLoaded ? 'block' : 'none', width: size, height: size }}
          className="rounded-lg"
        />
      </div>

      {label && (
        <p className="text-xs text-gray-500 text-center max-w-[200px] break-all">{label}</p>
      )}

      <button
        onClick={handleDownload}
        disabled={!qrLoaded}
        className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <i className="ri-download-2-line"></i>
        Descargar QR
      </button>
    </div>
  );
};

export default QRGenerator;
